import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import ContractDocumentSurface, {
  CONTRACT_A4_WIDTH,
  fieldColor,
  fieldIcon,
  type ContractFieldRenderer,
  type ContractVariableRenderer,
} from "./ContractDocumentSurface";
import AddressInputModal from "./AddressInputModal";
import type { InlineContractRendererRef, InlineContractValues } from "./InlineContractRenderer";
import { useDragPan } from "../../hooks/common/useDragPan";

const MIN_PAGE_SCALE = 0.35;
const MAX_PAGE_SCALE = 1.6;
const ZOOM_STEP = 0.15;
const ADDRESS_KEYS = new Set(["guardianAddress", "address", "addr"]);

type PinchState = {
  startDistance: number;
  startScale: number;
};

const clampScale = (value: number): number =>
  Math.min(MAX_PAGE_SCALE, Math.max(MIN_PAGE_SCALE, value));

const touchDistance = (touches: React.TouchList): number => {
  if (touches.length < 2) return 0;
  const a = touches[0];
  const b = touches[1];
  return Math.hypot(a.clientX - b.clientX, a.clientY - b.clientY);
};

export interface ContractProgressItem {
  /** DOM 앵커용 필드 키(data-field-key) */
  key: string;
  type: string;
  label: string;
  done: boolean;
}

export interface ContractProgress {
  total: number;
  done: number;
  items: ContractProgressItem[];
}

interface Props {
  contractDocJson?: string | null;
  variables?: Record<string, string | null | undefined> | null;
  initialAddress?: string;
  sealImageUrl?: string | null;
  signatureImageUrl?: string | null;
  readOnly?: boolean;
  className?: string;
  /** 필드 입력 상태가 바뀔 때마다 진행률을 부모로 올려준다(계약서 밖 진행 체커용). */
  onProgressChange?: (progress: ContractProgress) => void;
}

type FieldKind = {
  type: string;
  key: string;
  label?: string;
  required?: boolean;
};

export type RichContractRendererRef = InlineContractRendererRef & {
  getProgress: () => ContractProgress;
  /** 특정 필드로 부드럽게 스크롤 */
  scrollToField: (key: string) => void;
  /** 아직 안 채운 첫 필드로 스크롤 */
  scrollToNextIncomplete: () => void;
};

const parseDocJson = (raw?: string | null): any | null => {
  if (!raw) return null;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const textValue = (v: string | null | undefined): string =>
  v != null && v !== "" ? String(v) : "";

const fieldLabel = (type: string, label?: string, key?: string): string => {
  if (label && label.trim()) return label;
  if (type === "signature") return "보호자 서명";
  if (type === "seal") return "사업장 도장";
  if (type === "checkbox") return "동의";
  if (type === "date") return "날짜";
  if (type === "address") return "주소";
  return key || "입력";
};

const collectFields = (node: any, out: FieldKind[] = []): FieldKind[] => {
  if (!node) return out;
  if (node.type === "contractField") {
    const a = node.attrs || {};
    out.push({
      type: a.fieldType || "",
      key: a.fieldKey || "",
      label: a.label,
      // 단일레이어: inline 노드의 required 를 존중(선택 필드는 미체크 제출 허용)
      required: a.required !== false,
    });
  }
  if (node.type === "contractVariable") {
    const key = node.attrs?.varKey || "";
    if (ADDRESS_KEYS.has(key)) out.push({ type: "address", key, label: node.attrs?.label });
  }
  if (Array.isArray(node.content)) {
    node.content.forEach((child: any) => collectFields(child, out));
  }
  return out;
};

const RichContractRenderer = forwardRef<RichContractRendererRef, Props>(
  (
    {
      contractDocJson,
      variables,
      initialAddress,
      sealImageUrl,
      signatureImageUrl,
      readOnly = false,
      className,
      onProgressChange,
    },
    ref,
  ) => {
    const doc = useMemo(() => parseDocJson(contractDocJson), [contractDocJson]);
    const fieldKinds = useMemo(() => collectFields(doc), [doc]);
    const rootRef = useRef<HTMLDivElement | null>(null);
    const [containerWidth, setContainerWidth] = useState(CONTRACT_A4_WIDTH);
    const [fields, setFields] = useState<Record<string, string>>({});
    const [checkboxes, setCheckboxes] = useState<Record<string, boolean>>({});
    const [signature, setSignature] = useState<string | null>(null);
    const [signatureModalOpen, setSignatureModalOpen] = useState(false);
    const [addrPickerKey, setAddrPickerKey] = useState<string | null>(null);
    const [manualScale, setManualScale] = useState<number | null>(null);
    const pinchRef = useRef<PinchState | null>(null);
    const pan = useDragPan(); // PC 마우스 왼쪽 드래그 = 손바닥 팬

    useEffect(() => {
      const el = rootRef.current;
      if (!el) return;
      const update = () => setContainerWidth(el.clientWidth || CONTRACT_A4_WIDTH);
      update();
      const ro = new ResizeObserver(update);
      ro.observe(el);
      return () => ro.disconnect();
    }, []);

    useEffect(() => {
      const next: Record<string, string> = {};
      fieldKinds
        .filter((f) => f.type === "address" || ADDRESS_KEYS.has(f.key))
        .forEach((f) => {
          const v = textValue(variables?.[f.key]) || initialAddress || "";
          if (v) next[f.key] = v;
        });
      if (Object.keys(next).length) {
        setFields((prev) => ({ ...next, ...prev }));
      }
    }, [fieldKinds, initialAddress, variables]);

    // 계약서 밖 진행 체커용 — 보호자가 실제로 채워야 하는 필드만 집계(도장=사업장 몫이라 제외).
    const isDone = (f: FieldKind): boolean => {
      if (f.type === "signature") return !!(signature || signatureImageUrl);
      if (f.type === "checkbox") return !!checkboxes[f.key];
      if (f.type === "address" || ADDRESS_KEYS.has(f.key))
        return !!(fields[f.key] || textValue(variables?.[f.key]));
      if (f.type === "date" || f.type === "text") return !!fields[f.key];
      return true;
    };
    const progress: ContractProgress = useMemo(() => {
      const items: ContractProgressItem[] = fieldKinds
        .filter((f) => f.type !== "seal")
        .map((f) => ({
          key: f.key || `${f.type}`,
          type: f.type,
          label: fieldLabel(f.type, f.label, f.key),
          done: isDone(f),
        }));
      return { total: items.length, done: items.filter((i) => i.done).length, items };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [fieldKinds, fields, checkboxes, signature, signatureImageUrl, variables]);

    useEffect(() => {
      onProgressChange?.(progress);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [progress]);

    const scrollToField = (key: string) => {
      const el = rootRef.current?.querySelector<HTMLElement>(`[data-field-key="${key}"]`);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center", inline: "center" });
        el.classList.add("contract-field-flash");
        window.setTimeout(() => el.classList.remove("contract-field-flash"), 1400);
      }
    };
    const scrollToNextIncomplete = () => {
      const next = progress.items.find((i) => !i.done);
      if (next) scrollToField(next.key);
    };

    useImperativeHandle(ref, () => ({
      getValues: (): InlineContractValues => {
        const out: Record<string, string> = { ...fields };
        // 주소: 기본주소 + 상세주소 합쳐 하나의 변수 값으로
        fieldKinds
          .filter((f) => f.type === "address" || ADDRESS_KEYS.has(f.key))
          .forEach((f) => {
            const base = fields[f.key] || textValue(variables?.[f.key]) || "";
            const detail = fields[`${f.key}Detail`] || "";
            const full = [base, detail].filter(Boolean).join(" ");
            if (full) out[f.key] = full;
          });
        return { fields: out, checkboxes, signature };
      },
      getProgress: () => progress,
      scrollToField,
      scrollToNextIncomplete,
      validate: () => {
        for (const f of fieldKinds) {
          if ((f.type === "address" || ADDRESS_KEYS.has(f.key)) && !fields[f.key] && !variables?.[f.key]) {
            return "주소를 입력해주세요.";
          }
        }
        for (const f of fieldKinds) {
          // 단일레이어: '선택' 체크박스(required===false)는 미체크 제출 허용, '필수'만 강제.
          if (f.type === "checkbox" && f.required !== false && !checkboxes[f.key]) {
            return `'${fieldLabel(f.type, f.label, f.key)}' 항목에 체크해주세요.`;
          }
        }
        if (fieldKinds.some((f) => f.type === "signature") && !signature && !signatureImageUrl) {
          return "서명을 해주세요.";
        }
        return null;
      },
    }), [checkboxes, fieldKinds, fields, signature, signatureImageUrl, variables]);

    if (!doc) return null;

    const fitScale = clampScale(Math.min(1, Math.max(1, containerWidth - 8) / CONTRACT_A4_WIDTH));
    const pageScale = manualScale ?? fitScale;
    const zoomPercent = Math.round(pageScale * 100);
    const zoomOut = () => setManualScale((prev) => clampScale((prev ?? pageScale) - ZOOM_STEP));
    const zoomIn = () => setManualScale((prev) => clampScale((prev ?? pageScale) + ZOOM_STEP));
    const resetZoom = () => setManualScale(null);

    const setField = (key: string, value: string) =>
      setFields((prev) => ({ ...prev, [key]: value }));

    const startPinchZoom = (event: React.TouchEvent<HTMLDivElement>) => {
      if (event.touches.length !== 2) return;
      const distance = touchDistance(event.touches);
      if (distance <= 0) return;
      event.preventDefault();
      pinchRef.current = { startDistance: distance, startScale: pageScale };
    };

    const movePinchZoom = (event: React.TouchEvent<HTMLDivElement>) => {
      const pinch = pinchRef.current;
      if (!pinch || event.touches.length !== 2) return;
      const distance = touchDistance(event.touches);
      if (distance <= 0) return;
      event.preventDefault();
      setManualScale(clampScale(pinch.startScale * (distance / pinch.startDistance)));
    };

    const endPinchZoom = (event: React.TouchEvent<HTMLDivElement>) => {
      if (event.touches.length < 2) {
        pinchRef.current = null;
      }
    };

    const renderAddressControl = (
      keyName: string,
      label: string | undefined,
      key: string,
      filledValue?: string,
    ) => {
      const base = fields[keyName] || filledValue || "";
      const detail = fields[`${keyName}Detail`] ?? "";
      const full = [base, detail].filter(Boolean).join(" ");
      if (readOnly) {
        return (
          <span key={key} data-field-key={keyName} className="font-medium text-[#111] [overflow-wrap:anywhere]">
            {full || label || keyName}
          </span>
        );
      }
      // 탭하면 주소검색+상세주소 입력 모달을 연다.
      return (
        <button
          key={key}
          type="button"
          data-field-key={keyName}
          onClick={() => setAddrPickerKey(keyName)}
          className={`inline bg-transparent p-0 text-left text-[13px] underline decoration-dotted decoration-[#FFB330] underline-offset-2 [overflow-wrap:anywhere] ${
            full ? "text-[#111]" : "text-[#999]"
          }`}
        >
          {full || label || "주소 입력"}
        </button>
      );
    };

    const renderVariableValue: ContractVariableRenderer = (varKey, label, key) => {
      // 주소만 보호자 직접 입력. 그 외 변수는 자동채움 표시 전용 — 값 없으면 빈칸(라벨/입력칸 노출 금지).
      if (ADDRESS_KEYS.has(varKey)) {
        return renderAddressControl(varKey, label, key, textValue(variables?.[varKey]));
      }
      const value = textValue(variables?.[varKey]);
      return (
        <span key={key} className="font-medium text-[#111]">
          {value}
        </span>
      );
    };

    const renderFieldControl = (
      type: string,
      fieldKey: string,
      label: string,
      width: number,
      height: number,
    ) => {
      const color = fieldColor(type);
      if (type === "seal") {
        return sealImageUrl ? (
          <img
            src={sealImageUrl}
            alt={label}
            style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
          />
        ) : (
          <div className="flex h-full w-full flex-col items-center justify-center">
            <div
              className="flex items-center justify-center rounded-full border-2 border-dashed"
              style={{
                borderColor: color,
                color,
                width: "72%",
                height: "72%",
                fontSize: 11,
                fontWeight: 700,
              }}
            >
              {fieldIcon(type)}
            </div>
            <div className="mt-1 w-full truncate text-center text-[10px]" style={{ color }}>
              {label}
            </div>
          </div>
        );
      }
      if (type === "signature") {
        const image = signature || signatureImageUrl;
        return (
          <button
            type="button"
            disabled={readOnly}
            onClick={() => !readOnly && setSignatureModalOpen(true)}
            className={`flex h-full w-full items-center justify-center rounded text-[12px] font-semibold ${
              image
                ? "bg-transparent p-0"
                : "bg-transparent px-2 text-center text-[10px] leading-tight"
            }`}
            style={{ color }}
          >
            {image ? (
              <img src={image} alt={label} className="block h-full w-full object-contain" />
            ) : (
              <span>{fieldIcon(type)} {label}</span>
            )}
          </button>
        );
      }
      if (type === "checkbox") {
        const checked = !!checkboxes[fieldKey];
        return (
          <button
            type="button"
            disabled={readOnly}
            aria-label={label}
            title={label}
            onClick={() => setCheckboxes((prev) => ({ ...prev, [fieldKey]: !prev[fieldKey] }))}
            className="flex h-full w-full items-center justify-center rounded border-2 bg-white text-[#FF9934] disabled:cursor-default"
            style={{
              borderColor: color,
              fontSize: Math.max(12, Math.min(width, height) * 0.72),
              lineHeight: 1,
            }}
          >
            {checked ? "✓" : ""}
          </button>
        );
      }
      if (type === "address") {
        return renderAddressControl(fieldKey, label, `field-${fieldKey}`);
      }
      if (readOnly) {
        return <span className="text-[12px] text-[#333]">{fields[fieldKey] || variables?.[fieldKey] || label}</span>;
      }
      return (
        <input
          type={type === "date" ? "date" : "text"}
          value={fields[fieldKey] ?? ""}
          onChange={(e) => setField(fieldKey, e.target.value)}
          placeholder={label}
          className="h-full w-full rounded border-0 border-b-2 border-dashed bg-transparent px-1.5 text-[12px] focus:outline-none"
          style={{
            borderColor: color,
            color: "#111",
            minWidth: Math.min(width, 120),
            minHeight: Math.min(height, 34),
          }}
        />
      );
    };

    const renderContractField: ContractFieldRenderer = (node, key) => {
      const a = node.attrs || {};
      const type = a.fieldType || "text";
      const fieldKey = a.fieldKey || key;
      const label = fieldLabel(type, a.label, fieldKey);
      const color = fieldColor(type);

      // 단일레이어(inline) 필드: x/y 좌표가 없으면 본문 흐름 안에 inline 으로 렌더한다.
      // 필드 컨트롤(서명버튼·체크박스·입력)은 절대좌표 오버레이와 공유하되 래퍼만 inline.
      const isInline = a.x == null && a.y == null;
      if (isInline) {
        const iw =
          a.width ??
          (type === "signature"
            ? 150
            : type === "checkbox"
              ? 18
              : type === "seal"
                ? 64
                : 120);
        const ih =
          a.height ??
          (type === "signature"
            ? 40
            : type === "checkbox"
              ? 18
              : type === "seal"
                ? 64
                : 24);
        return (
          <span
            key={key}
            data-contract-field="true"
            data-field-type={type}
            data-field-key={fieldKey}
            className="inline-flex select-none rounded bg-white/30 align-middle"
            style={{
              width: iw,
              height: ih,
              outline: `1.5px dashed ${color}`,
              verticalAlign: "middle",
              boxSizing: "border-box",
              margin: "0 2px",
            }}
          >
            {renderFieldControl(type, fieldKey, label, iw, ih)}
          </span>
        );
      }

      const width = a.width ?? 200;
      const height = a.height ?? 80;
      return (
        <div
          key={key}
          data-contract-field="true"
          data-field-type={type}
          data-field-key={fieldKey}
          style={{
            position: "absolute",
            left: a.x ?? 300,
            top: a.y ?? 900,
            width,
            height,
            boxSizing: "border-box",
          }}
        >
          <div
            className="h-full w-full select-none rounded bg-white/30"
            style={{ outline: `1.5px dashed ${color}` }}
          >
            {renderFieldControl(type, fieldKey, label, width, height)}
          </div>
        </div>
      );
    };

    return (
      <div ref={rootRef} className={className}>
        <style>{`
          @keyframes contractFieldFlash {
            0%, 100% { box-shadow: 0 0 0 0 rgba(255,153,52,0); }
            30% { box-shadow: 0 0 0 4px rgba(255,153,52,0.55); }
          }
          .contract-field-flash {
            animation: contractFieldFlash 1.4s ease-in-out;
            border-radius: 4px;
          }
        `}</style>
        <div className="sticky top-0 z-20 mb-2 flex items-center justify-end gap-1 bg-white/95 py-1">
          <button
            type="button"
            onClick={zoomOut}
            disabled={pageScale <= MIN_PAGE_SCALE + 0.01}
            aria-label="축소"
            title="축소"
            className="flex h-8 w-8 items-center justify-center rounded border border-[#E9E9E9] bg-white text-[18px] font-semibold text-[#444] disabled:opacity-35"
          >
            −
          </button>
          <button
            type="button"
            onClick={resetZoom}
            aria-label="화면 맞춤"
            title="화면 맞춤"
            className="h-8 min-w-[58px] rounded border border-[#E9E9E9] bg-white px-2 text-[12px] font-semibold text-[#444]"
          >
            {zoomPercent}%
          </button>
          <button
            type="button"
            onClick={zoomIn}
            disabled={pageScale >= MAX_PAGE_SCALE - 0.01}
            aria-label="확대"
            title="확대"
            className="flex h-8 w-8 items-center justify-center rounded border border-[#E9E9E9] bg-white text-[18px] font-semibold text-[#444] disabled:opacity-35"
          >
            +
          </button>
        </div>
        <div
          ref={pan.ref}
          className="pb-1"
          style={{
            maxHeight: "70vh",
            overflow: "auto",
            cursor: "grab",
            overscrollBehavior: "contain",
          }}
          onMouseDown={pan.onMouseDown}
          onMouseMove={pan.onMouseMove}
          onMouseUp={pan.onMouseUp}
          onMouseLeave={pan.onMouseLeave}
          onTouchStart={startPinchZoom}
          onTouchMove={movePinchZoom}
          onTouchEnd={endPinchZoom}
          onTouchCancel={endPinchZoom}
        >
          <ContractDocumentSurface
            doc={doc}
            scale={pageScale}
            variables={variables}
            renderVariable={renderVariableValue}
            renderField={renderContractField}
            clipPages={false}
          />
        </div>

        <AddressInputModal
          open={addrPickerKey != null}
          initialBase={
            addrPickerKey
              ? fields[addrPickerKey] || textValue(variables?.[addrPickerKey]) || ""
              : ""
          }
          initialDetail={addrPickerKey ? fields[`${addrPickerKey}Detail`] || "" : ""}
          onClose={() => setAddrPickerKey(null)}
          onComplete={(result) => {
            if (!addrPickerKey) return;
            setField(addrPickerKey, result.address);
            setField(`${addrPickerKey}Detail`, result.detail);
            setField(`${addrPickerKey}_zonecode`, result.zonecode);
            setField(`${addrPickerKey}_roadAddress`, result.roadAddress);
            setField(`${addrPickerKey}_jibunAddress`, result.jibunAddress);
            if (result.buildingName) setField(`${addrPickerKey}_buildingName`, result.buildingName);
            setAddrPickerKey(null);
          }}
        />

        {signatureModalOpen && (
          <SignatureCaptureModal
            onClose={() => setSignatureModalOpen(false)}
            onSave={(dataUrl) => {
              setSignature(dataUrl);
              setSignatureModalOpen(false);
            }}
          />
        )}
      </div>
    );
  },
);

RichContractRenderer.displayName = "RichContractRenderer";

const SignatureCaptureModal = ({
  onClose,
  onSave,
}: {
  onClose: () => void;
  onSave: (dataUrl: string) => void;
}) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const drawing = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = "#111";
    ctx.lineWidth = 2.5;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
  }, []);

  const pointOf = (e: React.MouseEvent | React.TouchEvent) => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const clientX = "touches" in e ? e.touches[0].clientX : e.clientX;
    const clientY = "touches" in e ? e.touches[0].clientY : e.clientY;
    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const start = (e: React.MouseEvent | React.TouchEvent) => {
    e.preventDefault();
    drawing.current = true;
    const ctx = canvasRef.current!.getContext("2d")!;
    const p = pointOf(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const move = (e: React.MouseEvent | React.TouchEvent) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current!.getContext("2d")!;
    const p = pointOf(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    setHasDrawn(true);
  };

  const end = () => {
    drawing.current = false;
  };

  const clear = () => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext("2d");
    if (!canvas || !ctx) return;
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    setHasDrawn(false);
  };

  const save = () => {
    if (!hasDrawn) {
      alert("서명을 먼저 입력해 주세요.");
      return;
    }
    onSave(canvasRef.current!.toDataURL("image/png"));
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-end justify-center bg-black/50 md:items-center">
      <div className="w-full bg-white p-5 shadow-xl md:max-w-md md:rounded-2xl">
        <div className="mb-3 text-[16px] font-bold text-[#111]">서명</div>
        <canvas
          ref={canvasRef}
          width={600}
          height={240}
          className="h-auto w-full touch-none rounded-lg border border-[#E9E9E9] bg-white"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
        <div className="mt-4 flex gap-2">
          <button
            type="button"
            onClick={clear}
            className="flex-1 rounded-full border border-[#E9E9E9] py-2.5 text-[12px] text-[#666]"
          >
            지우기
          </button>
          <button
            type="button"
            onClick={onClose}
            className="flex-1 rounded-full border border-[#E9E9E9] py-2.5 text-[12px] text-[#666]"
          >
            취소
          </button>
          <button
            type="button"
            onClick={save}
            className="flex-[2] rounded-full bg-[#FF9934] py-2.5 text-[12px] font-semibold text-white"
          >
            서명 사용
          </button>
        </div>
      </div>
    </div>
  );
};

export default RichContractRenderer;
